import { useGamification } from "../hooks/useGamification";
import { BADGE_META, buttonStyles, panelStyle, sectionTitle, subtleText, tokens } from "../lib/ui";

export function GamificationPage() {
  const { badges, newlyAwarded, loading, checking, error, checkMilestones } = useGamification();

  // Pre-compute the set of earned badge IDs for O(1) lookup
  const earnedSet = new Set(badges.map((b) => b.badge_id));

  if (loading) {
    return (
      <div className="page-loading">
        <p style={subtleText}>Loading badge vault...</p>
      </div>
    );
  }

  return (
    <div className="inner-page__container" style={{ gap: tokens.space.lg }}>
      {/* Hero */}
      <section
        style={{
          ...panelStyle,
          background: "linear-gradient(135deg, #fefce8 0%, #fffbeb 100%)",
          border: "1px solid #fde68a",
        }}
      >
        <div style={{ fontSize: tokens.fontSize.xs, textTransform: "uppercase", letterSpacing: "0.1em", color: tokens.color.gold, fontWeight: tokens.fontWeight.bold }}>
          Badge Vault
        </div>
        <h2 style={{ margin: "0.4rem 0", fontSize: tokens.fontSize["3xl"] }}>
          Your achievements & milestones
        </h2>
        <p style={{ margin: 0, ...subtleText }}>
          Each badge reflects a moment in your practice journey. Keep showing up — the next one is closer than you think.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, marginTop: tokens.space.sm, flexWrap: "wrap" }}>
          <button
            onClick={() => void checkMilestones()}
            disabled={checking}
            style={buttonStyles.primary}
          >
            {checking ? "Checking..." : "Check for New Badges"}
          </button>
          <span style={subtleText}>{badges.length} earned · {Object.keys(BADGE_META).length} total</span>
        </div>
      </section>

      {error && (
        <p style={{ color: tokens.color.error, background: "#fef2f2", borderRadius: tokens.radius.md, padding: tokens.space.sm }}>
          {error}
        </p>
      )}

      {/* Newly awarded toast */}
      {newlyAwarded.length > 0 && (
        <section
          style={{
            ...panelStyle,
            background: tokens.color.accentLight,
            border: "1px solid #86efac",
          }}
        >
          <strong style={{ color: tokens.color.accent }}>🎉 Newly awarded!</strong>
          <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap", marginTop: tokens.space.xs }}>
            {newlyAwarded.map((badgeId) => {
              const meta = BADGE_META[badgeId];
              return (
                <span
                  key={badgeId}
                  style={{
                    background: meta ? `${meta.color}18` : "#f3f4f6",
                    border: `1px solid ${meta ? `${meta.color}40` : "#d1d5db"}`,
                    borderRadius: tokens.radius.full,
                    padding: "0.3rem 0.75rem",
                    fontSize: tokens.fontSize.sm,
                    fontWeight: tokens.fontWeight.semibold,
                  }}
                >
                  {meta ? `${meta.emoji} ${meta.label}` : badgeId}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Badge grid — earned and locked */}
      <section style={panelStyle}>
        <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>All Badges</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: tokens.space.sm,
          }}
        >
          {Object.entries(BADGE_META).map(([key, meta]) => {
            const earned = earnedSet.has(key);
            const earnedAt = badges.find((b) => b.badge_id === key)?.earned_at;
            return (
              <BadgeCard
                key={key}
                emoji={meta.emoji}
                label={meta.label}
                description={meta.description}
                color={meta.color}
                earned={earned}
                earnedAt={earnedAt}
              />
            );
          })}
        </div>
      </section>

      {/* Earned timeline */}
      {badges.length > 0 && (
        <section style={panelStyle}>
          <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>Earn History</h3>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            {[...badges]
              .sort((a, b) => new Date(b.earned_at).getTime() - new Date(a.earned_at).getTime())
              .map((badge) => {
                const meta = BADGE_META[badge.badge_id];
                return (
                  <div
                    key={badge.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      padding: "0.6rem 0",
                      borderBottom: `1px solid ${tokens.color.border}`,
                    }}
                  >
                    <span style={{ fontSize: "1.5rem" }}>{meta?.emoji ?? "🏅"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: tokens.fontWeight.semibold }}>{meta?.label ?? badge.badge_id}</div>
                      <div style={subtleText}>{new Date(badge.earned_at).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}
    </div>
  );
}

function BadgeCard({
  emoji,
  label,
  description,
  color,
  earned,
  earnedAt,
}: {
  emoji: string;
  label: string;
  description: string;
  color: string;
  earned: boolean;
  earnedAt?: string;
}) {
  return (
    <div
      style={{
        borderRadius: tokens.radius.md,
        padding: tokens.space.sm,
        border: `1px solid ${earned ? `${color}50` : tokens.color.border}`,
        background: earned ? `${color}10` : tokens.color.mutedLight,
        opacity: earned ? 1 : 0.55,
        transition: "opacity 0.2s",
      }}
    >
      <div style={{ fontSize: "2rem", marginBottom: "0.4rem", filter: earned ? "none" : "grayscale(1)" }}>
        {emoji}
      </div>
      <div style={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm }}>{label}</div>
      <p style={{ ...subtleText, marginTop: "0.2rem", fontSize: tokens.fontSize.xs }}>{description}</p>
      {earned && earnedAt && (
        <div
          style={{
            marginTop: "0.4rem",
            fontSize: tokens.fontSize.xs,
            color: color,
            fontWeight: tokens.fontWeight.semibold,
          }}
        >
          Earned {new Date(earnedAt).toLocaleDateString()}
        </div>
      )}
      {!earned && (
        <div style={{ marginTop: "0.4rem", fontSize: tokens.fontSize.xs, color: tokens.color.muted }}>
          Not yet unlocked
        </div>
      )}
    </div>
  );
}
